import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './AuthPage.css';
import { supabase } from '../services/supabaseClient';

/**
 * PRD-037 Task 2.2: Reset Password Page
 * Allows users to set a new password after clicking reset link
 * PRD-037 Task 2.4: Frontend password validation matching backend requirements
 */
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [tokenError, setTokenError] = useState(false);

  // PRD-037 Task 2.4: Password validation matching backend requirements
  const validatePassword = (pwd) => {
    const errors = [];

    if (!pwd || typeof pwd !== 'string') {
      return ['Password is required'];
    }

    if (pwd.length < 12) {
      errors.push('At least 12 characters');
    }

    if (!/[A-Z]/.test(pwd)) {
      errors.push('One uppercase letter');
    }

    if (!/[a-z]/.test(pwd)) {
      errors.push('One lowercase letter');
    }

    if (!/[0-9]/.test(pwd)) {
      errors.push('One number');
    }

    // eslint-disable-next-line no-useless-escape
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      errors.push('One special character');
    }

    const commonPatterns = ['password', '123456', 'qwerty', 'abc123', 'letmein', 'welcome'];
    const lowerPwd = pwd.toLowerCase();
    if (commonPatterns.some(pattern => lowerPwd.includes(pattern))) {
      errors.push('No common patterns');
    }

    return errors;
  };

  // Extract tokens from URL hash (Supabase redirects with hash params)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const access = hashParams.get('access_token') || searchParams.get('access_token');
    const refresh = hashParams.get('refresh_token') || searchParams.get('refresh_token');
    const type = hashParams.get('type') || searchParams.get('type');

    if (access && refresh && type === 'recovery') {
      setAccessToken(access);
      setRefreshToken(refresh);
    } else {
      // Try to get session from Supabase (might already be set)
      supabase.auth.getSession().then(({ data }) => {
        if (data?.session) {
          setAccessToken(data.session.access_token);
          setRefreshToken(data.session.refresh_token);
        } else {
          setTokenError(true);
        }
      });
    }
  }, [searchParams]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setError('');

    // Real-time validation feedback
    const errors = validatePassword(newPassword);
    setValidationErrors(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    const errors = validatePassword(password);
    if (errors.length > 0) {
      setError('Password does not meet requirements.');
      setValidationErrors(errors);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      // Try direct Supabase update first
      if (accessToken && refreshToken) {
        // Set session from recovery tokens
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (sessionError) {
          throw new Error('Invalid or expired reset link. Please request a new one.');
        }
      }

      // Update password via Supabase
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw new Error(updateError.message || 'Password reset failed.');
      }

      // Sign out after password change to force re-login
      await supabase.auth.signOut();

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="page-container auth-page" style={{
        maxWidth: '500px',
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}>
        <div className="auth-form-container card-style" style={{ width: '100%' }}>
          <h2>Invalid Reset Link</h2>
          <p style={{ color: '#ff4b4b', marginBottom: '20px' }}>
            This password reset link is invalid or has expired.
          </p>
          <p style={{ color: '#b0b8c1', marginBottom: '20px' }}>
            Please request a new password reset link.
          </p>
          <Link to="/forgot-password" className="button" style={{ display: 'block', textAlign: 'center' }}>
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page-container auth-page" style={{
        maxWidth: '500px',
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}>
        <div className="auth-form-container card-style" style={{ width: '100%' }}>
          <h2>Password Reset Successful</h2>
          <p style={{ color: '#00e6d2', marginBottom: '20px' }}>
            Your password has been updated successfully.
          </p>
          <p style={{ color: '#b0b8c1', marginBottom: '20px' }}>
            Please log in with your new password.
          </p>
          <Link to="/login" className="button" style={{ display: 'block', textAlign: 'center' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container auth-page" style={{
      maxWidth: '500px',
      margin: '0 auto',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '80vh'
    }}>
      <div className="auth-form-container card-style" style={{ width: '100%' }}>
        <h2>Set New Password</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter your new password"
                className={error && validationErrors.length > 0 ? 'error' : ''}
                required
                autoComplete="new-password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            {/* Password Requirements Checklist */}
            <div style={{ marginTop: '10px', fontSize: '0.85rem' }}>
              <p style={{ color: '#b0b8c1', marginBottom: '5px' }}>Password requirements:</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { req: 'At least 12 characters', met: password.length >= 12 },
                  { req: 'One uppercase letter', met: /[A-Z]/.test(password) },
                  { req: 'One lowercase letter', met: /[a-z]/.test(password) },
                  { req: 'One number', met: /[0-9]/.test(password) },
                  // eslint-disable-next-line no-useless-escape
                  { req: 'One special character', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) }
                ].map((item, idx) => (
                  <li key={idx} style={{
                    color: item.met ? '#00e6d2' : '#b0b8c1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    marginBottom: '2px'
                  }}>
                    <span>{item.met ? '✓' : '○'}</span>
                    {item.req}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
              }}
              placeholder="Confirm your new password"
              className={error && password !== confirmPassword ? 'error' : ''}
              required
              autoComplete="new-password"
              disabled={isLoading}
            />
            {confirmPassword && password !== confirmPassword && (
              <span className="error-text">Passwords do not match</span>
            )}
          </div>

          {error && <p className="error-message">{error}</p>}

          <button
            type="submit"
            className="button"
            disabled={isLoading || validationErrors.length > 0 || password !== confirmPassword}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
        <p className="auth-switch-message">
          Remember your password? <Link to="/login">Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
